import React, { useMemo, useState } from 'react';
import { Btn, Msg, Section, panelPost } from '../kit';
import { Labeled, inputCls } from '../email/emailKit';
import {
  type Note, PreviewFrame, S, SaveBar, type SearchSetting, type TabProps, Toggle, move, nextKey, useDraft,
} from './shared';

/**
 * Search chips (owner, 2026-10-12) — the keyword chips under the homepage
 * search box ("Battery storage IP", …) and the pool the "Surprise Me" button
 * picks from at random. Was hard-coded in NewHome.tsx. POST
 * homepage/save_search replaces both lists (order = display order); repeats
 * (ignoring case) are dropped. An empty pool hides the Surprise button;
 * switched off, the whole chip row is hidden. The public /homepage/content is
 * cached for 30 s, so a save shows within half a minute. A click still logs
 * the keyword (search_bubble / search_surprise_me, Search → Homepage searches).
 */

type Chip = { text: string; key: string };
type Draft = { enabled: boolean; bubbles: Chip[]; surprise: Chip[]; surprise_label: string };

/** homepage_content.DEFAULT_SEARCH — used when the server predates the section. */
const BUILT_IN: SearchSetting = {
  enabled: true,
  bubbles: ['Battery storage IP', 'Drug delivery patents', 'Computer vision tech'],
  surprise: [
    'Battery storage IP', 'Drug delivery patents', 'Computer vision tech', 'iot device for farming',
    'india agriculture', 'sustainable development', 'wearable technology', 'education tech',
  ],
  surprise_label: 'Surprise Me',
};

const chips = (list: any): Chip[] =>
  (Array.isArray(list) ? list : []).map(t => ({ text: String(t ?? ''), key: nextKey() }));

const draftFrom = (s: SearchSetting | undefined): Draft => {
  const v = s || BUILT_IN;
  return {
    enabled: v.enabled !== false,
    bubbles: chips(v.bubbles),
    surprise: chips(v.surprise),
    surprise_label: v.surprise_label || '',
  };
};

/** Trimmed, inner spaces collapsed — what the server stores. */
const clean = (t: string) => t.replace(/\s+/g, ' ').trim();

/** The kept keywords (first spelling wins) and the repeats the save drops. */
const dedupe = (list: Chip[]): { kept: string[]; repeats: string[] } => {
  const seen = new Set<string>();
  const kept: string[] = [];
  const repeats: string[] = [];
  list.forEach(c => {
    const t = clean(c.text);
    if (!t) return;
    const k = t.toLowerCase();
    if (seen.has(k)) { repeats.push(t); return; }
    seen.add(k);
    kept.push(t);
  });
  return { kept, repeats };
};

/** The chip row as the homepage draws it, under a dummy search box. */
const ChipsPreview: React.FC<{ d: Draft }> = ({ d }) => {
  const shown = dedupe(d.bubbles).kept;
  const pool = dedupe(d.surprise).kept;
  const label = d.surprise_label.trim() || 'Surprise Me';
  return (
    <PreviewFrame note={d.enabled ? undefined : 'switched off — the homepage shows the search box without chips'}
      dim={!d.enabled}>
      <div className="max-w-xl mx-auto">
        <div className="w-full pl-11 pr-4 py-3 rounded-full border border-grayscale-300 bg-white text-grayscale-1000 text-sm">
          Search by tech, field or keyword. Press ENTER.
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {shown.map(kw => (
            <span key={kw}
              className="px-3 sm:px-4 py-1.5 rounded-full bg-white border border-primary-500 text-grayscale-1200 text-xs sm:text-sm">
              {kw}
            </span>
          ))}
          {pool.length > 0 && (
            <span className="px-3 sm:px-4 py-1.5 rounded-full bg-primary-600 text-white text-xs sm:text-sm font-medium"
              title={`Picks one of ${pool.length} keyword${pool.length === 1 ? '' : 's'} at random`}>
              {label}
            </span>
          )}
          {!shown.length && !pool.length && <span className="text-sm text-grayscale-1100">No chips yet.</span>}
        </div>
        <div className="mt-3 text-[11px] text-grayscale-1100 text-center">
          {pool.length
            ? `“${label}” runs one of ${pool.length} keyword${pool.length === 1 ? '' : 's'} at random.`
            : 'The Surprise pool is empty, so the button is hidden.'}
        </div>
      </div>
    </PreviewFrame>
  );
};

/** One ordered list of keywords: type, move, delete, add. */
const ChipList: React.FC<{
  title: string; hint: string; list: Chip[]; max: number; maxLen: number; placeholder: string; addLabel: string;
  onChange: (next: Chip[]) => void;
}> = ({ title, hint, list, max, maxLen, placeholder, addLabel, onChange }) => {
  const set = (key: string, text: string) => onChange(list.map(c => (c.key === key ? { ...c, text } : c)));
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div className="text-xs text-grayscale-1100">
        <span className="font-bold text-grayscale-1300">{title}</span> — {list.length} · at most {max} · {hint}
      </div>
      {list.map((c, i) => (
        <div key={c.key} className="flex items-center gap-2 rounded-lg border border-grayscale-300 p-2">
          <span className="text-xs text-grayscale-1100 w-5 text-right shrink-0">{i + 1}</span>
          <div className="flex-1 min-w-0">
            <input value={c.text} maxLength={maxLen} aria-label={`${title} ${i + 1}`}
              onChange={e => set(c.key, e.target.value)} placeholder={placeholder} className={inputCls} />
          </div>
          <span className="flex items-center gap-1 shrink-0">
            <Btn small onClick={() => onChange(move(list, i, -1))} disabled={i === 0} title="Move up">↑</Btn>
            <Btn small onClick={() => onChange(move(list, i, 1))} disabled={i === list.length - 1} title="Move down">↓</Btn>
            <Btn small kind="danger" onClick={() => onChange(list.filter(x => x.key !== c.key))}
              title="Delete (Discard brings it back)">Delete</Btn>
          </span>
        </div>
      ))}
      <div>
        <Btn small onClick={() => onChange([...list, { text: '', key: nextKey() }])} disabled={list.length >= max}
          title={list.length >= max ? `At most ${max}` : undefined}>
          {addLabel}
        </Btn>
      </div>
    </div>
  );
};

const SearchChips: React.FC<TabProps> = ({ data, onSaved, onDirty }) => {
  const saved = data.settings.search;
  const { draft, edit, dirty, discard, settle } = useDraft<SearchSetting | undefined, Draft>(saved, draftFrom, onDirty);
  const [busy, setBusy] = useState<'' | 'save' | 'reset'>('');
  const [msg, setMsg] = useState<Note>(null);
  const maxBubbles = data.limits?.bubbles || 6;
  const maxPool = data.limits?.surprise || 30;
  const maxLen = data.limits?.chip_len || 60;
  const maxLabel = data.limits?.surprise_label_len || 30;
  const builtIn = data.search_defaults || BUILT_IN;

  const setField = (patch: Partial<Draft>) => edit(d => ({ ...d, ...patch }));
  const bubbleInfo = useMemo(() => dedupe(draft.bubbles), [draft.bubbles]);
  const poolInfo = useMemo(() => dedupe(draft.surprise), [draft.surprise]);
  const repeats = [...bubbleInfo.repeats, ...poolInfo.repeats];
  /** Chips not in the pool — a hint only, both lists stand on their own. */
  const notInPool = bubbleInfo.kept.filter(k => !poolInfo.kept.some(p => p.toLowerCase() === k.toLowerCase()));

  const save = async () => {
    const blankChip = draft.bubbles.findIndex(c => !clean(c.text));
    if (blankChip >= 0) { setMsg({ kind: 'error', text: `Chip ${blankChip + 1} is empty — type a keyword or delete it.` }); return; }
    const blankPool = draft.surprise.findIndex(c => !clean(c.text));
    if (blankPool >= 0) {
      setMsg({ kind: 'error', text: `Surprise keyword ${blankPool + 1} is empty — type a keyword or delete it.` });
      return;
    }
    if (!bubbleInfo.kept.length) { setMsg({ kind: 'error', text: 'Add at least one chip, or switch the chips off.' }); return; }
    if (bubbleInfo.kept.length > maxBubbles) { setMsg({ kind: 'error', text: `At most ${maxBubbles} chips.` }); return; }
    if (poolInfo.kept.length > maxPool) { setMsg({ kind: 'error', text: `At most ${maxPool} Surprise keywords.` }); return; }
    setBusy('save'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'save_search', {
      enabled: draft.enabled,
      bubbles: draft.bubbles.map(c => clean(c.text)),
      surprise: draft.surprise.map(c => clean(c.text)),
      surprise_label: draft.surprise_label.trim(),
    });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Save failed' }); return; }
    onSaved(next);
    settle();
    setMsg({ kind: 'ok', text: 'Saved — the homepage picks it up within half a minute.' });
  };

  const reset = async () => {
    setBusy('reset'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'reset', { section: 'search' });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Reset failed' }); return; }
    onSaved(next);
    settle();
    setMsg({
      kind: 'ok',
      text: `Back to the built-in ${builtIn.bubbles.length} chips and ${builtIn.surprise.length} Surprise keywords.`,
    });
  };

  const nowLine = !saved ? 'the built-in chips (this server has no Search chips setting yet).'
    : !saved.enabled ? 'the chips are switched off, so only the search box shows.'
    : `${saved.bubbles.length} chip${saved.bubbles.length === 1 ? '' : 's'}` +
      (saved.surprise.length
        ? ` and “${saved.surprise_label || 'Surprise Me'}” picking from ${saved.surprise.length} keyword${saved.surprise.length === 1 ? '' : 's'}.`
        : ', no Surprise button (the pool is empty).');

  return (
    <Section title="Search chips">
      <p className="text-xs text-grayscale-1100 -mt-1">
        The keyword chips under the homepage search box, and the pool the Surprise button picks one keyword from at
        random. A click runs that search. Keep the chips short: three fit on one line on a phone.
      </p>
      <div className="text-xs text-grayscale-1200">On the homepage now: {nowLine}</div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <div className="flex items-center gap-2">
        <Toggle on={draft.enabled} onChange={v => setField({ enabled: v })} label="Show the chips" />
        <span className="text-sm text-grayscale-1200">Show the chips</span>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChipList title="Chips on the homepage" hint="shown in this order" list={draft.bubbles} max={maxBubbles}
          maxLen={maxLen} placeholder="e.g. Battery storage IP" addLabel="+ Add chip"
          onChange={next => setField({ bubbles: next })} />
        <div className="flex flex-col gap-3 min-w-0">
          <ChipList title="Surprise pool" hint="empty hides the button" list={draft.surprise} max={maxPool}
            maxLen={maxLen} placeholder="e.g. wearable technology" addLabel="+ Add keyword"
            onChange={next => setField({ surprise: next })} />
          <div className="max-w-xs">
            <Labeled label="Surprise button text" hint="blank = Surprise Me">
              <input value={draft.surprise_label} maxLength={maxLabel} placeholder="Surprise Me"
                onChange={e => setField({ surprise_label: e.target.value })} className={inputCls} />
            </Labeled>
          </div>
        </div>
      </div>

      {repeats.length > 0 && (
        <div className="text-xs text-amber-700">
          Repeated (ignoring capitals), dropped on save: {repeats.map(r => `“${r}”`).join(', ')}.
        </div>
      )}
      {notInPool.length > 0 && poolInfo.kept.length > 0 && (
        <div className="text-[11px] text-grayscale-1100">
          Not in the Surprise pool: {notInPool.map(r => `“${r}”`).join(', ')} — fine if that is on purpose.
        </div>
      )}

      <ChipsPreview d={draft} />

      <SaveBar dirty={dirty} busy={busy} onSave={save} onDiscard={() => { discard(); setMsg(null); }} onReset={reset} />
    </Section>
  );
};

export default SearchChips;
